import React from 'react';
import { ME_ID } from './data.jsx';
// home.jsx - Home dashboard. The default landing page for Triage.
//
// Shows each team member what needs their attention today, across four cards:
//   1. My open tickets (from state.tickets, filtered to me)
//   2. My tasks - 5 most recently created, any status (was: overdue only, but
//      most days that card was empty which made the dashboard feel dead)
//   3. Unassigned tickets in the queue (nudge to triage)
//   4. Recent KB activity (last 5 articles edited)
//
// Clicking any item deep-links to the relevant view. Empty states acknowledge
// the win rather than apologise for nothing being there.
  const { useState, useEffect } = React;

  const homeApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'homeApi');

  // ── helpers ────────────────────────────────────────────────────────────────
  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const fmtDate = (d = new Date()) =>
    d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  // Tasks store assignedTo as comma-separated slugs ("alex,james")
  const parseTaskAssignees = s => (!s ? [] : s.split(',').map(x => x.trim()).filter(Boolean));

  // Friendly label for the task status pill on the home card. Mirrors the
  // canonical statuses from tasks.jsx so the dashboard reads the same way as
  // the task list itself.
  const TASK_STATUS_META = {
    'pending':     { label: 'Pending',     bg: '#FEF3C7', fg: '#92400E' },
    'in-progress': { label: 'In progress', bg: '#EFF6FF', fg: '#1E40AF' },
    'completed':   { label: 'Completed',   bg: '#D1FAE5', fg: '#065F46' },
  };

  // Ticket priority sort - Critical → Low. Falls back to newest first.
  const TICKET_PRIO_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortTicketsForCard = (a, b) => {
    const pa = TICKET_PRIO_ORDER[(a.priority || '').toLowerCase()] ?? 9;
    const pb = TICKET_PRIO_ORDER[(b.priority || '').toLowerCase()] ?? 9;
    if (pa !== pb) return pa - pb;
    return (b.created || 0) - (a.created || 0);
  };


  // ── shared sub-components ──────────────────────────────────────────────────
  // Card accepts EITHER a plain `title` (with optional `count` badge) OR a
  // custom `header` JSX node (e.g. tab switcher). If `header` is provided it
  // wins, and title/count are ignored.
  const Card = ({ title, count, accent, children, footer, empty, header }) => (
    <div style={{
      background: 'var(--bg-card, #fff)',
      border: '1px solid var(--line)',
      borderRadius: 10,
      padding: '12px 16px',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 240,
      minWidth: 0,
    }}>
      {header ? (
        <div style={{ marginBottom: 14 }}>{header}</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)', letterSpacing: 0.2 }}>
            {title}
          </h3>
          {count != null && (
            <span style={{
              marginLeft: 10,
              background: accent || 'var(--bg-sub)',
              color: accent ? '#fff' : 'var(--text-muted)',
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 10,
              minWidth: 18,
              textAlign: 'center',
            }}>
              {count}
            </span>
          )}
        </div>
      )}
      {/* No overflow here on purpose. The card's fixed height comfortably
          fits its content (5 list items + footer), so nothing needs to
          scroll. Adding overflow-y also flips overflow-x to auto, which
          collides with the list rows' negative margins and spawns a stray
          horizontal scrollbar. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {count === 0 && empty ? (
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 12,
          }}>
            {empty}
          </div>
        ) : children}
      </div>
      {footer && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--line)' }}>
          {footer}
        </div>
      )}
    </div>
  );

  // Pill-style tab button used by the KB card header.
  const TabButton = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      style={{
        background: active ? 'var(--bg-sub)' : 'transparent',
        border: 'none',
        padding: '6px 10px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
        color: active ? 'var(--text)' : 'var(--text-muted)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );

  const ListItem = ({ onClick, left, right, sub }) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 6px',
        margin: '0 -6px',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 13,
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-sub)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {left}
        </div>
        {sub && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {sub}
          </div>
        )}
      </div>
      {right && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{right}</div>
      )}
    </div>
  );

  const FooterLink = ({ onClick, label }) => (
    <div
      onClick={onClick}
      style={{
        fontSize: 12, fontWeight: 500, color: 'var(--accent-text, #0F766E)',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
      }}
    >
      {label} →
    </div>
  );

  // ── main view ──────────────────────────────────────────────────────────────
  function HomeView({ state, dispatch, currentUser }) {
    const [tasks, setTasks] = useState(null);
    const [tasksError, setTasksError] = useState(null);
    // KB card tab toggle. Resets to 'latest' on every navigation in.
    const [kbTab, setKbTab] = useState('latest');

    useEffect(() => {
      homeApi('GET', '/tasks')
        .then(rows => setTasks(Array.isArray(rows) ? rows : []))
        .catch(err => { console.warn('[home] tasks fetch failed:', err.message); setTasksError(err); setTasks([]); });
    }, []);

    const firstName = (currentUser?.displayName || '').split(' ')[0] || 'there';

    // Under pagination, state.tickets only contains the current tickets-page
    // - so the Home tiles need their own dedicated slim fetches (top ~15
    // rows each). Cheap even at 10K+ tickets. Refreshes on mount + when a
    // global 'tickets need refresh' event fires (matches the app-wide one).
    const [myTickets, setMyTickets] = useState([]);
    const [unassigned, setUnassigned] = useState([]);
    useEffect(() => {
      const load = () => {
        if (ME_ID) {
          homeApi('GET', `/tickets?view=inbox&assignee=${encodeURIComponent(ME_ID)}&size=15`)
            .then(r => setMyTickets(Array.isArray(r?.rows) ? r.rows.sort(sortTicketsForCard) : []))
            .catch(err => console.warn('[home] my tickets fetch failed:', err.message));
        }
        homeApi('GET', '/tickets?view=unassigned&size=15')
          .then(r => setUnassigned(Array.isArray(r?.rows) ? r.rows.sort(sortTicketsForCard) : []))
          .catch(err => console.warn('[home] unassigned fetch failed:', err.message));
      };
      load();
      const onRefresh = () => load();
      window.addEventListener('triage:tickets-need-refresh', onRefresh);
      return () => window.removeEventListener('triage:tickets-need-refresh', onRefresh);
    }, []);

    // ── card 2 data: my tasks (most recent) ─────────────────────────────────
    // Show the 5 most-recently-created tasks I'm on, regardless of status, so
    // the card always has signal - recent completions, in-flight work, and
    // anything new I've been assigned. Sort key is createdAt desc; falls
    // back to id desc if createdAt is missing on legacy rows.
    // Rob's feature request 23 Jun 2026: exclude completed tasks from the
    // Home tile. Home is a glance view - recently-finished work is noise
    // there. Users who want the full picture click through to To-Do List.
    const myRecentTasks = (tasks || [])
      .filter(t => !t.trashed && t.status !== 'completed' && parseTaskAssignees(t.assignedTo).includes(ME_ID))
      .sort((a, b) => {
        const ad = a.createdAt || '';
        const bd = b.createdAt || '';
        if (ad !== bd) return bd.localeCompare(ad);
        return (b.id || 0) - (a.id || 0);
      });

    // (myTickets + unassigned are populated by the dedicated fetches above.)

    // ── card 4 data: latest KB + my favourites tabs ─────────────────────────
    const recentKb = [...(state.kbArticles || [])]
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
      .slice(0, 4);
    const myFavouriteKb = (state.kbArticles || [])
      .filter(a => a.isFavourited)
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
      .slice(0, 4);
    const kbDisplayed = kbTab === 'favourites' ? myFavouriteKb : recentKb;
    const kbTotalCount = kbTab === 'favourites'
      ? (state.kbArticles || []).filter(a => a.isFavourited).length
      : (state.kbArticles || []).length;

    // ── navigation helpers ──────────────────────────────────────────────────
    // Uses path-based routing now (no '#'). pushState doesn't fire popstate,
    // so we synthesise one - the URL→State effect in app.jsx listens for it
    // and updates state.view accordingly.
    const navigateTo = (path) => {
      window.history.pushState(null, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
    const openTicket = (id) => navigateTo(`/tickets/${id}`);
    const openKbArticle = (id) => navigateTo(`/kb/${id}`);
    const goToView = (path) => navigateTo(path);

    // ── date helpers for display ────────────────────────────────────────────
    const daysAgo = (mins) => {
      // tickets store `created` as minutes ago (per data.jsx convention)
      if (mins == null) return '';
      const m = Math.abs(mins);
      if (m < 60) return `${Math.round(m)}m ago`;
      if (m < 60 * 24) return `${Math.round(m / 60)}h ago`;
      return `${Math.round(m / (60 * 24))}d ago`;
    };

    // Relative "Nd ago" style for task createdAt (ISO string). Mirrors the
    // shape of `daysAgo` so the My-tasks card reads consistently next to
    // My-tickets which uses minute-offsets.
    const relativeSince = (iso) => {
      if (!iso) return '';
      const ms = Date.now() - new Date(iso).getTime();
      if (isNaN(ms) || ms < 0) return '';
      const m = Math.floor(ms / 60000);
      if (m < 60) return `${m}m ago`;
      if (m < 60 * 24) return `${Math.floor(m / 60)}h ago`;
      return `${Math.floor(m / (60 * 24))}d ago`;
    };

    // Compact coloured status pill shown on the right of each My-tasks row.
    const taskStatusPill = (status) => {
      const meta = TASK_STATUS_META[status] || { label: status || '-', bg: 'var(--bg-sub)', fg: 'var(--text-muted)' };
      return (
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
          fontSize: 10, fontWeight: 600, background: meta.bg, color: meta.fg,
          textTransform: 'uppercase', letterSpacing: 0.3,
        }}>{meta.label}</span>
      );
    };

    const kbDate = (a) => {
      // Use the original createdAt (ISO string) - that's the honest "when was
      // this written" date. Always include the year because legacy KB articles
      // span years; "29 May" alone would be ambiguous for a 2019 import.
      if (!a.createdAt) return '';
      const d = new Date(a.createdAt);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
      // Outer wrapper owns vertical scroll. `.main` is overflow:hidden (to
      // keep the task board's horizontal scroll from bleeding to the body),
      // so each top-level view has to provide its own scroll container or
      // content past the viewport gets clipped. Settings does the same.
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <div style={{ padding: '20px 28px', maxWidth: 1280, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <h1 style={{
            margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text)',
            fontFamily: 'Poppins, var(--font), sans-serif', letterSpacing: -0.3,
          }}>
            {greeting()}, {firstName}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-muted)' }}>
            {fmtDate()}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 14,
        }}>
          {/* Card 1: My open tickets */}
          <Card
            title="My open tickets"
            count={myTickets.length}
            accent={myTickets.length > 0 ? 'var(--accent, #0D9488)' : null}
            empty="Nothing on your plate. Nice."
            footer={myTickets.length > 4 && (
              <FooterLink label={`View all ${myTickets.length}`} onClick={() => goToView('/my-tickets')}/>
            )}
          >
            {myTickets.slice(0, 4).map(t => (
              <ListItem
                key={t.id}
                onClick={() => openTicket(t.id)}
                left={<><span style={{ color: 'var(--text-muted)', marginRight: 6 }}>#{t.id}</span>{t.subject}</>}
                sub={t.requesterName || t.requester || ''}
                right={daysAgo(t.created)}
              />
            ))}
          </Card>

          {/* Card 2: My tasks (5 most recent, any status) */}
          <Card
            title="My tasks"
            count={tasks === null ? null : myRecentTasks.length}
            accent={myRecentTasks.length > 0 ? 'var(--accent, #0D9488)' : null}
            empty={tasks === null ? null : (tasksError ? 'Could not load tasks.' : 'No tasks assigned to you yet.')}
            footer={myRecentTasks.length > 4 && (
              <FooterLink label={`View all ${myRecentTasks.length}`} onClick={() => goToView('/tasks')}/>
            )}
          >
            {tasks === null ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--text-muted)', fontSize: 12 }}>
                Loading…
              </div>
            ) : (
              myRecentTasks.slice(0, 4).map(t => (
                <ListItem
                  key={t.id}
                  onClick={() => goToView('/tasks')}
                  left={t.title}
                  sub={t.category || ''}
                  right={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      {taskStatusPill(t.status)}
                      <span style={{ color: 'var(--text-muted)' }}>{relativeSince(t.createdAt)}</span>
                    </span>
                  }
                />
              ))
            )}
          </Card>

          {/* Card 3: Unassigned in queue */}
          <Card
            title="Unassigned in queue"
            count={unassigned.length}
            accent={unassigned.length > 0 ? '#F59E0B' : null}
            empty="Queue is clear."
            footer={unassigned.length > 4 && (
              <FooterLink label={`View all ${unassigned.length}`} onClick={() => goToView('/unassigned')}/>
            )}
          >
            {unassigned.slice(0, 4).map(t => (
              <ListItem
                key={t.id}
                onClick={() => openTicket(t.id)}
                left={<><span style={{ color: 'var(--text-muted)', marginRight: 6 }}>#{t.id}</span>{t.subject}</>}
                sub={t.requesterName || t.requester || ''}
                right={daysAgo(t.created)}
              />
            ))}
          </Card>

          {/* Card 4: Knowledge base - Latest / My favourites tabs */}
          <Card
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <TabButton active={kbTab === 'latest'} onClick={() => setKbTab('latest')}>
                  Latest
                </TabButton>
                <TabButton active={kbTab === 'favourites'} onClick={() => setKbTab('favourites')}>
                  <span style={{ color: kbTab === 'favourites' ? '#F59E0B' : 'inherit' }}>★</span>
                  My favourites
                </TabButton>
              </div>
            }
            empty={kbTab === 'favourites'
              ? 'No favourites yet. Star an article in the KB to pin it here.'
              : 'No KB articles yet.'}
            count={kbDisplayed.length === 0 ? 0 : null}
            footer={
              kbTab === 'favourites' && kbTotalCount > 4 ? (
                <FooterLink label={`View all ${kbTotalCount} favourites`} onClick={() => goToView('/kb')}/>
              ) : (
                // Always render a footer link so the KB card height matches
                // between Latest and Favourites tabs (otherwise the row
                // collapses when the favourites tab has no "view all" link).
                <FooterLink label="Browse knowledge base" onClick={() => goToView('/kb')}/>
              )
            }
          >
            {kbDisplayed.map(a => (
              <ListItem
                key={a.id}
                onClick={() => openKbArticle(a.id)}
                left={a.title}
                sub={a.summary || ''}
                right={kbDate(a)}
              />
            ))}
          </Card>
        </div>
        </div>
      </div>
    );
  }

export { HomeView };